import PropTypes from 'prop-types';
import { useState, useRef, useMemo } from 'react';
import styles from './FJMobilePreviewModal.module.less';
import CloseIcon from '../../../assets/svg/mobile-close.svg?react';
import FJImage from '../../../common/components/FJImage/FJImage';
import FJSlideCompareIcon from '../../../assets/svg/slideCompare.svg?react';
import FJComparisonIcon from '../../../assets/svg/comparison.svg?react';
import ReloadIcon from '../../../assets/svg/reload.svg?react';
import FJImageIcon from '../../../assets/svg/mobile-preview-image.svg?react';
import FJAiImageIcon from '../../../assets/svg/mobil-preview-ai-image.svg?react';
import classNames from 'classnames';
import FJConfig from '../../../config/FJConfig';
import FJAppConstant from '../../../constants/FJAppConstant';
import FJMobilePreviewFooter from './FJMobilePreviewFooter/FJMobilePreviewFooter';
import FJLocalStore from '../../../store/FJLocalStore';
import FJConstant from '../../../constants/FJConstant';

const FJMobilePreviewModal = props => {
    const {
        onClose,
        data,
        visible,
        shareToSociety,
        onDownload,
        onDelete,
        switchPreview,
        previewList,
        isShowOrder = true,
    } = props;
    const { imgUrl, originalUrl, id } = data;
    const containerRef = useRef(null);
    const imageRef = useRef(null);
    const originalImageRef = useRef(null);

    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [stateIsComparison, setStateIsComparison] = useState(false);
    const [stateSlideCompareScale, setStateSlideCompareScale] = useState(50);
    const slideComparePositionRef = useRef(0);
    const [stateSlideCompareType, setStateSlideCompareType] = useState(FJConstant.COMPARE);

    const [stateImgLoadStatus, setStateImgLoadStatus] = useState(FJConstant.LOADING);
    const [stateOriginImgLoadStatus, setStateOriginImgLoadStatus] = useState(FJConstant.LOADING);
    //#region 对比组件是否展示状态
    const isShowComparison = useMemo(() => {
        return (
            FJConfig.appType !== FJAppConstant.TEXT_TO_IMAGE &&
            FJConfig.appType !== FJAppConstant.IMAGE_EXTENDER &&
            FJConfig.appType !== FJAppConstant.IMAGE_ENHANCER &&
            FJConfig.appType !== FJAppConstant.PHOTO_RESTORATION &&
            FJConfig.appType !== FJAppConstant.PHOTO_COLORIZE
        );
    }, []);

    const canSlideCompare = useMemo(() => {
        return (
            FJConfig.appType === FJAppConstant.IMAGE_ENHANCER ||
            FJConfig.appType === FJAppConstant.PHOTO_RESTORATION ||
            FJConfig.appType === FJAppConstant.PHOTO_COLORIZE
        );
    }, []);
    // 滑杆组件显示
    const isShowSlideCompare = useMemo(() => {
        return (
            stateSlideCompareType === FJConstant.COMPARE &&
            canSlideCompare &&
            stateImgLoadStatus === FJConstant.SUCCESS &&
            stateOriginImgLoadStatus === FJConstant.SUCCESS
        );
    }, [stateSlideCompareType, canSlideCompare, stateImgLoadStatus, stateOriginImgLoadStatus]);
    // 滑杆footer按钮显示
    const isShowSlideCompareFooter = useMemo(() => {
        return (
            canSlideCompare &&
            stateImgLoadStatus === FJConstant.SUCCESS &&
            stateOriginImgLoadStatus === FJConstant.SUCCESS
        );
    }, [canSlideCompare, stateImgLoadStatus, stateOriginImgLoadStatus]);

    const isShowReload = useMemo(() => {
        return (
            stateImgLoadStatus === FJConstant.ERROR ||
            (stateOriginImgLoadStatus === FJConstant.ERROR && canSlideCompare)
        );
    }, [stateImgLoadStatus, stateOriginImgLoadStatus, canSlideCompare]);

    // 在主图展示loading
    const isLoading = useMemo(() => {
        return (
            stateImgLoadStatus === FJConstant.LOADING ||
            (stateOriginImgLoadStatus === FJConstant.LOADING && canSlideCompare)
        );
    }, [stateImgLoadStatus, stateOriginImgLoadStatus, canSlideCompare]);
    //#endregion

    // #region 初始化部分

    const imgBoxRect = useMemo(() => {
        let obj = {};
        if (containerRef.current) {
            const ratio = data.width / data.height;
            const containerW = containerRef.current.offsetWidth;

            const maxH = containerRef.current.offsetHeight - (canSlideCompare ? 244 : 140);
            let w = 0;
            let h = 0;
            if (ratio >= 1) {
                w = containerW;
                h = w / ratio;
            } else {
                w = containerW - 64;
                h = w / ratio;
            }
            if (h > maxH) {
                h = maxH;
                w = h * ratio;
            }
            if (w > 0 && h > 0) {
                obj = { height: h, width: w };
            }
        }
        return obj;
    }, [data, canSlideCompare]);

    // #endregion

    //#region 触摸切图事件
    // 处理触摸开始
    const _handleTouchStart = e => {
        setTouchStart({ x: e.targetTouches[0].clientX, y: e.targetTouches[0].clientY });
        setIsDragging(true);
    };

    // 处理触摸移动
    const _handleTouchMove = e => {
        if (!isDragging) return;
        setTouchEnd({ x: e.targetTouches[0].clientX, y: e.targetTouches[0].clientY });
    };

    // 处理触摸结束
    const _handleTouchEnd = () => {
        setIsDragging(false);

        if (!touchStart || !touchEnd) return;

        const horizontalDistance = touchStart.x - touchEnd.x;
        const verticalDistance = touchStart.y - touchEnd.y;
        const minHorizontalSwipeDistance = 50; // 水平最小滑动距离
        const minVerticalSwipeDistance = -70; // 向下垂直最小滑动距离

        if (Math.abs(horizontalDistance) > minHorizontalSwipeDistance) {
            //水平切换图片
            if (horizontalDistance > 0) {
                switchPreview(true);
            } else {
                switchPreview(false);
            }
        }
        if (verticalDistance < minVerticalSwipeDistance) {
            onClose();
        }
        setTouchStart(0);
        setTouchEnd(0);
    };
    //#endregion

    //#region 滑杆事件

    const _onSlideMouseDown = e => {
        e.stopPropagation();
        slideComparePositionRef.current = e.targetTouches[0].clientX;
        window.addEventListener('touchmove', _onSlideMouseMove);
        window.addEventListener('touchend', _onSlideMouseUp);
    };
    const _onSlideMouseMove = e => {
        e.stopPropagation();
        const delta = e.targetTouches[0].clientX - slideComparePositionRef.current;

        let scale = stateSlideCompareScale + Math.floor((delta / imgBoxRect?.width) * 100);

        scale = Math.min(Math.max(scale, 0), 100);
        setStateSlideCompareScale(scale);
    };
    const _onSlideMouseUp = () => {
        window.removeEventListener('touchmove', _onSlideMouseMove);
        window.removeEventListener('touchend', _onSlideMouseUp);
    };
    //#endregion

    //#region  渲染对比滑杆
    // 渲染滑动滑杆dom
    const _renderSlideCompare = () => {
        if (!isShowSlideCompare) return null;
        return (
            <div className={styles.slide_compare}>
                <div
                    className={classNames(styles.slide_compare_box)}
                    onTouchStart={_onSlideMouseDown}
                    style={{ left: stateSlideCompareScale + '%' }}
                >
                    <FJSlideCompareIcon className={styles.slide_compare_icon} />
                    <div className={styles.slide_compare_line} />
                </div>
            </div>
        );
    };
    // 渲染滑动footer
    const _renderSlideCompareFooter = () => {
        if (!isShowSlideCompareFooter) return null;
        return (
            <div className={styles.slide_compare_footer}>
                <div
                    className={classNames(styles.slide_compare_footer_box, {
                        [styles.active]: stateSlideCompareType === FJConstant.AFTER,
                    })}
                    onClick={() => {
                        setStateSlideCompareType(FJConstant.AFTER);
                    }}
                >
                    <FJImageIcon />
                    <div className={styles.compare_text}>{FJLocalStore._('before')}</div>
                </div>
                <div
                    className={classNames(styles.slide_compare_footer_box, {
                        [styles.active]: stateSlideCompareType === FJConstant.COMPARE,
                    })}
                    onClick={() => {
                        setStateSlideCompareType(FJConstant.COMPARE);
                    }}
                >
                    <FJComparisonIcon />
                    <div className={styles.compare_text}>{FJLocalStore._('compare')}</div>
                </div>
                <div
                    className={classNames(styles.slide_compare_footer_box, {
                        [styles.active]: stateSlideCompareType === FJConstant.BEFORE,
                    })}
                    onClick={() => {
                        setStateSlideCompareType(FJConstant.BEFORE);
                    }}
                >
                    <FJAiImageIcon />
                    <div className={styles.compare_text}>{FJLocalStore._('after')}</div>
                </div>
            </div>
        );
    };
    //#endregion

    //#region 渲染头部
    const _renderHeader = () => {
        const order = previewList.findIndex(item => item.id === id);
        return (
            <>
                <div className={styles.close} onClick={onClose}>
                    <CloseIcon />
                </div>
                <div className={styles.order} style={{ opacity: isShowOrder ? 1 : 0 }}>
                    {order + 1}/{previewList.length}
                </div>
            </>
        );
    };
    //#endregion

    //#region 渲染图片状态
    const imgStyleObj = useMemo(() => {
        const imgStyle = {};
        const originalImgStyle = {};
        if (isShowComparison && !isLoading) {
            originalImgStyle.opacity = stateIsComparison ? 1 : 0;
            imgStyle.opacity = !stateIsComparison ? 1 : 0;
        } else if (canSlideCompare && !isLoading) {
            originalImgStyle.opacity = stateSlideCompareType !== FJConstant.BEFORE ? 1 : 0;
            imgStyle.opacity = stateSlideCompareType !== FJConstant.AFTER ? 1 : 0;
            const imgScale = imgBoxRect?.width * (stateSlideCompareScale / 100);
            const originalImgScale = imgBoxRect?.width * (1 - stateSlideCompareScale / 100);
            originalImgStyle.clipPath =
                stateSlideCompareType === FJConstant.COMPARE ? `inset( 0px ${originalImgScale}px 0px 0px )` : 'none';
            imgStyle.clipPath =
                stateSlideCompareType === FJConstant.COMPARE ? `inset( 0px 0px 0px ${imgScale}px)` : 'none';
        } else {
            originalImgStyle.opacity = 0;
            imgStyle.opacity = 1;
        }
        return { imgStyle, originalImgStyle };
    }, [
        stateIsComparison,
        isShowComparison,
        canSlideCompare,
        stateSlideCompareScale,
        imgBoxRect,
        stateSlideCompareType,
        isLoading,
    ]);

    //#endregion

    //#region 渲染主图部分
    const _refresh = () => {
        imageRef.current && stateImgLoadStatus !== FJConstant.SUCCESS && imageRef.current.refresh();
        originalImageRef.current &&
            stateOriginImgLoadStatus !== FJConstant.SUCCESS &&
            originalImageRef.current.refresh();
    };
    const _renderMainImage = () => {
        return (
            <div className={styles.img_box} style={{ width: imgBoxRect?.width, height: imgBoxRect?.height }}>
                <FJImage
                    imgSrc={imgUrl}
                    boxClassName={classNames(styles.img)}
                    isShowLoading={true}
                    style={imgStyleObj.imgStyle}
                    isLoading={isLoading}
                    ref={imageRef}
                    loadCallback={type => {
                        setStateImgLoadStatus(type);
                    }}
                >
                    {isShowSlideCompare && <div className={styles.after}>{FJLocalStore._('after')}</div>}
                </FJImage>
                <FJImage
                    imgSrc={originalUrl}
                    boxClassName={classNames(styles.original_img)}
                    isShowLoading={true}
                    style={imgStyleObj.originalImgStyle}
                    ref={originalImageRef}
                    loadCallback={type => {
                        setStateOriginImgLoadStatus(type);
                    }}
                >
                    {isShowSlideCompare && <div className={styles.before}>{FJLocalStore._('before')}</div>}
                </FJImage>
                {isShowReload && (
                    <div className={styles.reload}>
                        <ReloadIcon onClick={_refresh} />
                    </div>
                )}
                {_renderSlideCompare()}
            </div>
        );
    };
    //#endregion

    return (
        <div
            className={classNames(styles.box, visible ? styles.fade_in : styles.fade_out)}
            ref={containerRef}
            style={{ paddingBottom: canSlideCompare ? 108 : 0 }}
            onTouchStart={_handleTouchStart}
            onTouchMove={_handleTouchMove}
            onTouchEnd={_handleTouchEnd}
        >
            {_renderHeader()}
            {_renderMainImage()}
            {_renderSlideCompareFooter()}
            <FJMobilePreviewFooter
                data={data}
                onDownload={onDownload}
                onDelete={onDelete}
                shareToSociety={shareToSociety}
                isShowComparison={isShowComparison}
                setIsComparison={setStateIsComparison}
                isComparison={stateIsComparison}
                canComparison={
                    stateOriginImgLoadStatus === FJConstant.SUCCESS && stateImgLoadStatus === FJConstant.SUCCESS
                }
            />
        </div>
    );
};
FJMobilePreviewModal.propTypes = {
    visible: PropTypes.bool,
    data: PropTypes.object,
    onClose: PropTypes.func,
    onDownload: PropTypes.func,
    onDelete: PropTypes.func,
    addAsScene: PropTypes.func,
    shareToSociety: PropTypes.func,
    isActiveNext: PropTypes.bool,
    isActivePrevious: PropTypes.bool,
    switchPreview: PropTypes.func,
    addAsSceneModalBtuStatus: PropTypes.string,
    previewList: PropTypes.array,
    isShowOrder: PropTypes.bool,
};
export default FJMobilePreviewModal;
